//pages/RefundPolicy.tsx
import Layout from "@/components/Layout";
// SEO: Import SEO component for refund policy structured data.
import SEO from '@/components/SEO';
import { Card, CardContent } from "@/components/ui/card";
import { RotateCcw, Mail } from "lucide-react";

export default function RefundPolicy() {
  const refundSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'Refund Policy',
    'url': 'https://tradesagepro.com/refund',
    'description': 'Refund Policy for TradeSagePro AI market research tokens and Neutral/No Output token return mechanism.',
    'about': { '@type': 'Organization', 'name': 'TradeSagePro' }
  };
  return (
    <Layout>
      {/* SEO TAGS: Refund policy indexed for transparency queries (brand + refund); WebPage schema clarifies purpose. */}
      <SEO
        title="Refund Policy | TradeSagePro AI Market Research Tokens"
        description="Understand TradeSagePro token purchases, Neutral/No Output automatic token returns, and refund limitations for AI market analysis outputs."
        canonical="https://tradesagepro.com/refund"
        type="article"
        jsonLd={refundSchema}
      />
      {/* END SEO TAGS */}
      <div className="min-h-screen bg-[hsl(226,26%,7%)] text-[hsl(210,9%,91%)] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <RotateCcw className="w-12 h-12 text-[hsl(193,100%,50%)] mr-3" />
              <h1 className="text-4xl font-bold text-white">Refund Policy</h1>
            </div>
            <p className="text-sm text-[hsl(210,9%,80%)]">Last Updated: December 19, 2025</p>
          </div>

          <Card className="bg-[hsl(215,15%,18%)] border-gray-700 animate-slide-in">
            <CardContent className="p-8 max-w-none">
              <div className="space-y-10 text-[hsl(210,9%,91%)] leading-relaxed">

                <section id="intro">
                  <p>
                    JINOVEX LLC ("Company", "we", "our", or "us") operates the website <span className="text-[hsl(193,100%,50%)]">www.tradesagepro.com</span> (the "Service").
                  </p>
                  <p className="mt-3">
                    This Refund Policy explains how refunds are handled for purchases of digital access tokens and under what conditions a monetary refund may or may not be issued.
                  </p>
                </section>

                <section id="credit-purchases">
                  <h2 className="text-2xl font-bold text-white mb-3">1. Digital Token Purchases</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Users purchase digital access tokens that provide access to AI-generated analytical outputs within the Service.</li>
                    <li>Tokens are not real money, have no intrinsic monetary value, and cannot be exchanged or redeemed outside the platform.</li>
                  </ul>
                </section>

                <section id="right-to-refund">
                  <h2 className="text-2xl font-bold text-white mb-3">2. Right to Refund (14-Day Cooling-Off Period)</h2>
                  <p className="mb-3">In accordance with applicable consumer protection regulations, customers may request a refund within 14 days of purchase, only if:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>The purchased tokens have not been used, and</li>
                    <li>The Service has not been accessed beyond minimal evaluation or trial use.</li>
                  </ul>
                </section>

                <section id="no-refund-after-use">
                  <h2 className="text-2xl font-bold text-white mb-3">3. No Refund After Service Use</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Once a user uses digital tokens to generate AI analysis outputs, the Service is considered delivered and consumed.</li>
                    <li>Refund requests based on dissatisfaction with results, performance, accuracy, or usefulness of the AI-generated outputs are not eligible for a monetary refund.</li>
                    <li>Requests such as "the analysis did not work for me", "I did not profit", or "the results were not helpful" do not constitute valid grounds for a refund.</li>
                  </ul>
                </section>

                <section id="neutral-no-output">
                  <h2 className="text-2xl font-bold text-white mb-3">4. Neutral / No Output Mechanism</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>If the AI system produces a Neutral / No Output result, the tokens used for that request are automatically returned to the user's in-platform token balance.</li>
                    <li>Returned tokens are not a monetary refund and do not affect the statutory refund conditions described above.</li>
                  </ul>
                </section>

                <section id="refund-processing">
                  <h2 className="text-2xl font-bold text-white mb-3">5. Refund Processing</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Eligible refunds (within the 14-day period and before token usage) will be issued to the original payment method.</li>
                    <li>All refund requests are reviewed individually and may require verification of token usage and access logs.</li>
                  </ul>
                </section>

                <section id="contact">
                  <h2 className="text-2xl font-bold text-white mb-3">6. Contact Information</h2>
                  <p className="mb-3">For refund-related questions or requests, please contact:</p>
                  <div className="mt-3 grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44] inline-flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      <a className="text-[hsl(193,100%,50%)] hover:underline" href="mailto:info@tradesagepro.com">info@tradesagepro.com</a>
                    </div>
                    <div className="p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                      <div className="font-semibold text-white mb-1">Registered Address</div>
                      <div className="text-[hsl(210,9%,85%)]">JINOVEX LLC<br/>Tbilisi, Georgia<br/>(Operating globally)</div>
                    </div>
                  </div>
                </section>

              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
